import type React from 'react';

export type IconButtonVariant = 'ghost' | 'key' | 'overlay' | 'neutral' | 'ghost-key' | 'ghost-inverse';
export type IconButtonSize = 'small' | 'medium' | 'large';
export type IconButtonState = 'enabled' | 'disabled' | 'pressed';

export interface IconButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'disabled'> {
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  state?: IconButtonState;
  icon: React.ReactNode;
  'aria-label': string; // Required for accessibility
}

const IconButton: React.FC<IconButtonProps> = ({
  variant = 'ghost',
  size = 'medium',
  state = 'enabled',
  icon,
  className = '',
  onClick,
  'aria-label': ariaLabel,
  ...props
}) => {
  const getButtonClasses = () => {
    const baseClasses = [
      'inline-flex',
      'items-center',
      'justify-center',
      'rounded-full',
      'transition-all',
      'duration-200',
      'ease-out',
      'overflow-hidden',
      'flex-shrink-0',
      'transform-gpu',
      'will-change-transform'
    ];

    // Size classes - matches the HTML dump dimensions
    const sizeClasses = {
      small: ['w-9', 'h-9'], // 36px
      medium: ['w-10', 'h-10'], // 40px  
      large: ['w-12', 'h-12'] // 48px
    };

    // Variant and state classes
    const variantStateClasses = {
      ghost: {
        enabled: [
          'bg-transparent',
          'hover:bg-disabled-fill',
          'text-action-primary',
          'hover:text-action-key-default',
          'transition-all',
          'duration-200',
          'ease-out'
        ],
        disabled: [
          'bg-transparent',
          'text-content-muted',
          'cursor-not-allowed'
        ],
        pressed: [
          'bg-disabled-fill',
          'text-action-primary'
        ]
      },
      key: {
        enabled: [
          'bg-action-key-default',
          'hover:bg-action-key-darker',
          'text-white',
          'shadow-sm',
          'hover:shadow-lg',
          'transition-all',
          'duration-200',
          'ease-out'
        ],
        disabled: [
          'bg-disabled-fill',
          'text-content-muted',
          'cursor-not-allowed'
        ],
        pressed: [
          'bg-action-key-darker',
          'text-white',
          'shadow-sm'
        ]
      },
      overlay: {
        enabled: [
          'bg-overlay-light',
          'backdrop-blur-md',
          'hover:bg-overlay-light-hover',
          'text-action-primary',
          'shadow-sm',
          'hover:shadow-lg',
          'hover:backdrop-blur-lg',
          'hover:text-action-key-darker',
          'transition-all',
          'duration-200',
          'ease-out'
        ],
        disabled: [
          'bg-overlay-light',
          'backdrop-blur-md',
          'text-content-muted',
          'cursor-not-allowed'
        ],
        pressed: [
          'bg-overlay-light-hover',
          'backdrop-blur-md',
          'text-action-key-darker',
          'shadow-sm'
        ]
      },
      neutral: {
        enabled: [
          'bg-neutral-fill-lighter',
          'hover:bg-neutral-fill-medium',
          'text-content-secondary',
          'hover:text-content-primary',
          'transition-all',
          'duration-200',
          'ease-out'
        ],
        disabled: [
          'bg-disabled-fill',
          'text-content-muted',
          'cursor-not-allowed'
        ],
        pressed: [
          'bg-neutral-fill-medium',
          'text-content-secondary'
        ]
      },
      'ghost-key': {
        enabled: [
          'bg-transparent',
          'hover:bg-disabled-fill',
          'text-action-key-default',
          'hover:text-action-key-darker',
          'transition-all',
          'duration-200',
          'ease-out'
        ],
        disabled: [
          'bg-transparent',
          'text-content-muted',
          'cursor-not-allowed'
        ],
        pressed: [
          'bg-disabled-fill',
          'text-action-key-darker'
        ]
      },
      'ghost-inverse': {
        enabled: [
          'bg-transparent',
          'hover:bg-white/20',
          'text-white',
          'hover:bg-white/30',
          'transition-all',
          'duration-200',
          'ease-out'
        ],
        disabled: [
          'bg-transparent',
          'text-white/40',
          'cursor-not-allowed'
        ],
        pressed: [
          'bg-white/20',
          'text-white'
        ]
      }
    };

    return [
      ...baseClasses,
      ...sizeClasses[size],
      ...variantStateClasses[variant][state],
      className
    ].filter(Boolean).join(' ');
  };

  const getIconSize = () => {
    const iconSizeMap = {
      small: 'w-5 h-5', // 20px
      medium: 'w-6 h-6', // 24px (26px from design but 24 is standard)
      large: 'w-8 h-8' // 32px
    };
    return iconSizeMap[size];
  };

  const isDisabled = state === 'disabled';

  return (
    <button
      className={getButtonClasses()}
      disabled={isDisabled}
      onClick={isDisabled ? undefined : onClick}
      aria-label={ariaLabel}
      {...props}
    >
      <div className={`${getIconSize()} flex items-center justify-center`}>
        {icon}
      </div>
    </button>
  );
};

export default IconButton;